/*
  Beyond the Fridge — For Business (screens 27 and 28).

  1. Prompt cards (blocks/btf-biz-prompt.liquid): copy the prompt to the clipboard, reveal the
     card's other face on tap (rollover and focus are CSS), and the Enlarge lightbox. The copy
     and fallback behaviour is lifted from the logic class at the end of
     designs/For Business - Design With AI.dc.html.
  2. The business board tool (sections/btf-biz-cyo-order.liquid): a subclass of the consumer
     tool in assets/btf-cyo.js (imported, never edited) with the business room geometry,
     ex-VAT prices, the quantity, the PO reference and the proof checkbox on the cart line.
*/

/* ------------------------------------------------------------- prompt cards */

function copyFallback(body, done) {
  const ta = document.createElement('textarea');
  ta.value = body;
  ta.setAttribute('style', 'position:fixed;top:0;left:0;opacity:0;');
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand('copy');
    done();
  } catch (err) {
    /* clipboard unavailable */
  }
  document.body.removeChild(ta);
}

function copyPrompt(btn) {
  const card = btn.closest('[data-prompt]');
  const text = card && card.querySelector('[data-prompt-text]');
  if (!text) return;
  const body = text.textContent.trim();
  const done = () => {
    const was = btn.textContent;
    btn.textContent = btn.dataset.copiedLabel || 'Copied';
    btn.classList.add('is-copied');
    setTimeout(() => {
      btn.textContent = was;
      btn.classList.remove('is-copied');
    }, 1600);
  };
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(body).then(done, () => copyFallback(body, done));
  } else {
    copyFallback(body, done);
  }
}

let zoomDialog = null;

function ensureZoom() {
  if (zoomDialog) return zoomDialog;
  const d = document.createElement('dialog');
  d.className = 'btf-biz-zoom';
  d.setAttribute('aria-label', 'Enlarged board artwork');
  d.innerHTML =
    '<img class="btf-biz-zoom__img" alt="">' +
    '<div class="btf-biz-zoom__row"><span class="btf-biz-zoom__caption"></span>' +
    '<button type="button" class="btf-biz-zoom__close">Close</button></div>';
  d.addEventListener('click', (e) => {
    // The backdrop and the Close button close it; the picture itself does not.
    if (e.target === d || e.target.closest('.btf-biz-zoom__close')) d.close();
  });
  document.body.appendChild(d);
  zoomDialog = d;
  return d;
}

function openZoom(src, caption) {
  if (!src) return;
  const d = ensureZoom();
  const img = d.querySelector('.btf-biz-zoom__img');
  img.src = src;
  img.alt = caption;
  d.querySelector('.btf-biz-zoom__caption').textContent = caption;
  if (typeof d.showModal === 'function') d.showModal();
  else d.setAttribute('open', '');
}

document.addEventListener('click', (e) => {
  const copy = e.target.closest('[data-copy]');
  if (copy) {
    e.stopPropagation();
    copyPrompt(copy);
    return;
  }
  const enlarge = e.target.closest('[data-enlarge], [data-btf-zoom]');
  if (enlarge) {
    e.stopPropagation();
    const art = enlarge.closest('[data-art]') || enlarge.closest('[data-prompt]') || enlarge.parentElement;
    const img = enlarge.tagName === 'IMG' ? enlarge : art && art.querySelector('img');
    if (!img) return;
    openZoom(img.currentSrc || img.src, enlarge.dataset.zoomCaption || img.dataset.zoom || img.alt || '');
    return;
  }
  // A tap on the card flips it where there is no hover (rollover and focus are CSS).
  const card = e.target.closest('[data-prompt]');
  if (card && !e.target.closest('button, a')) card.classList.toggle('is-open');
});

/* ------------------------------------------------------- business board tool */

// Measured against btf-cyo-room-biz-wall.png: the desk spans 15–86% of the frame and is
// 1200 mm wide, so 1% of the square is 16.9 mm. Boards hang centred over the desk.
// (04-create-your-own.md; ROOM in designs/Create Your Own - For Business.dc.html.)
const BIZ_ROOM = {
  Landscape: { Large: [20.9, 12.9, 59.2, 44.4], Medium: [28.3, 24.8, 44.4, 32.5], Small: [34.2, 33.6, 32.5, 23.7] },
  Portrait: { Large: [28.3, 2.8, 44.4, 59.2], Medium: [34.2, 15.6, 32.5, 44.4], Small: [38.7, 24.8, 23.7, 32.5] },
};

async function defineBizTool() {
  const host = document.querySelector('btf-biz-cyo[data-cyo-module]');
  if (!host || customElements.get('btf-biz-cyo')) return;
  try {
    await import(host.dataset.cyoModule);
  } catch (e) {
    console.warn('[btf-biz] the consumer board tool module could not be loaded:', e);
    return;
  }
  const Base = customElements.get('btf-cyo-tool');
  if (!Base) return;

  class BtfBizCyo extends Base {
    // Same crop model and bleed maths; only the wall changes.
    geom() {
      const g = super.geom();
      if (g.room) g.rect = BIZ_ROOM[this.state.orient][this.state.size];
      return g;
    }

    // Ex-VAT pence computed in Liquid (price | divided_by: 1.2); the design's figures otherwise.
    pricePence(size) {
      const v = this.variantFor(this.state.orient, size);
      if (v && typeof v.exVat === 'number') return v.exVat;
      return Math.round(super.pricePence(size) / 1.2);
    }

    fmtShort(p) {
      return '£' + (p / 100).toFixed(2);
    }

    qty() {
      const v = parseInt(this.refs.qty ? this.refs.qty.value : '1', 10);
      return v >= 1 ? Math.min(v, 999) : 1;
    }

    bind() {
      super.bind();
      if (this.refs.qty) this.refs.qty.addEventListener('input', () => this.render());
    }

    render() {
      super.render();
      const g = this.geom();
      const r = this.refs;
      const caps = r.caps.style;
      if (g.room) {
        // On the office wall the caps are drawn on the board rectangle, not on a plate.
        caps.left = g.rect[0] + '%';
        caps.top = g.rect[1] + '%';
        caps.width = g.rect[2] + '%';
        caps.height = g.rect[3] + '%';
        const dot = g.cap === 'white' ? '#f4f3f1' : '#232323';
        caps.backgroundImage = Array(4)
          .fill('radial-gradient(circle, ' + dot + ' 0 40%, rgba(0,0,0,.18) 41%, transparent 62%)')
          .join(', ');
        caps.backgroundSize = '9px 9px';
        caps.backgroundPosition = '4% 5%, 96% 5%, 4% 95%, 96% 95%';
        r.clip.style.boxShadow = '0 16px 30px rgba(45,38,30,.20), 0 2px 5px rgba(45,38,30,.18)';
      } else {
        caps.left = '0';
        caps.top = '0';
        caps.width = '100%';
        caps.height = '100%';
        caps.backgroundSize = '100% 100%';
        caps.backgroundPosition = '0 0';
        r.clip.style.boxShadow = 'none';
      }
      if (r.lineTotal) {
        const q = this.qty();
        r.lineTotal.textContent = q > 1 ? q + ' boards · ' + this.fmtBig(this.pricePence(this.state.size) * q) + ' ex VAT' : '';
        r.lineTotal.hidden = q <= 1;
      }
    }

    // The cart line: quantity, PO reference and proof travel with the artwork properties.
    baseForm(v, g, flag) {
      const fd = super.baseForm(v, g, flag);
      fd.set('quantity', String(this.qty()));
      const po = ((r) => (r && r.value ? r.value : ''))(this.refs.po).trim();
      if (po) fd.append('properties[PO reference]', po);
      fd.append('properties[Proof required]', this.refs.proof && this.refs.proof.checked ? 'Yes' : 'No');
      return fd;
    }
  }

  customElements.define('btf-biz-cyo', BtfBizCyo);
}

defineBizTool();
